import { useGate, useUnit } from 'effector-react';
import {
  Autocomplete,
  FormControl,
  FormLabel,
  TextField,
  CircularProgress,
  FormHelperText,
  Typography,
} from '@mui/material';
import { withControl } from '@features/effector-form/hoc/withControl';
import {
  RelationAccountResidentsControlGate,
  relationAccountResidentsQuery,
} from './model';
import type { Gate, Resident } from './types';

export const RelationAccountResidentsControl = withControl<Gate>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    isError,
    propertyGuid,
    accountId,
  }) => {
    useGate(RelationAccountResidentsControlGate, { propertyGuid, accountId });
    const { data, pending } = useUnit(relationAccountResidentsQuery);

    const findedValue =
      value !== ''
        ? data?.find(
            (item: Resident) =>
              item.id === (typeof value === 'object' ? value?.id : value)
          ) ?? null
        : null;

    const handleChange = (_: unknown, newValue: any) => setValue(newValue || null);

    return (
      <FormControl required={required} disabled={disabled}>
        {label && <FormLabel error={isError}>{label}</FormLabel>}
        <Autocomplete
          loading={pending}
          value={findedValue}
          options={data && propertyGuid && accountId ? data : []}
          onChange={handleChange}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                readOnly,
                error: isError,
                endAdornment: readOnly ? null : (
                  <>
                    {pending && <CircularProgress size={20} color="inherit" />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={({ key, ...props }, option) => {
            const isExistPhoneComma = Boolean(option?.email);

            return (
              <li key={key} {...props}>
                <div>
                  <Typography variant="body2" color="text.primary">
                    {option.label}
                  </Typography>
                  {(option.phone || option.email) && (
                    <Typography variant="body2" color="text.secondary">
                      {`${
                        option.phone
                          ? `${option.phone}${isExistPhoneComma ? ', ' : ''}`
                          : ''
                      }${option.email ?? ''}`}
                    </Typography>
                  )}
                </div>
              </li>
            );
          }}
          readOnly={readOnly}
          disabled={disabled}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
    );
  }
);
